import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';
import AdminPanel from './Users';
import LogsPanel from './Logs';
import AdminServer from './Servers/Servers';
import PacientPanel from './Pacients';
import AppointmentPanel from './Appointment/Appointment';
import OrganizationPanel from './Organizations/Organization';
import BranchPanel from './Branches/Branch';
import ServicePanel from './Services/Services';
import ModalityPanel from './Modalities/Modalities';
import EquipmentPanel from './Equipment/Equipment';
import SlotPanel from './Slot/Slot';
import SettingsPanel from './Settings/Settings';
import ConfigurationPanel from './Configuration/ConfigurationPanel';
import WLConfiguration from './WLConfiguration'; // <--- Import nuevo
import BotConnection from './BotConnection';
import TemplatesPanel from './Templates/Templates';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>One or more of the requested studies are not available at this time.</h4>
        {showStudyList && (
          <p className="mt-2">
            Return to the{' '}
            <Link
              className="text-primary-light"
              to="/"
            >
              study list
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkList, servicesManager, extensionManager },
  };
  const InRoutesFront = [
    {
      path: '/pacients',
      children: ({ servicesManager }) => <PacientPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },

    {
      path: '/configuration/logs',
      children: ({ servicesManager }) => <LogsPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/appointments',
      children: ({ servicesManager }) => <AppointmentPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration',
      children: ({ servicesManager }) => <ConfigurationPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/general',
      children: ({ servicesManager }) => <SettingsPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/wl-presets',
      children: ({ servicesManager }) => <WLConfiguration servicesManager={servicesManager} />,
      roles: ['admin', 'medico general', 'radiologiaInterno', 'medicoExterno'],
    },
    {
      path: '/configuration/organizations',
      children: ({ servicesManager }) => <OrganizationPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/users',
      children: ({ servicesManager }) => <AdminPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/branches',
      children: ({ servicesManager }) => <BranchPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/server',
      children: ({ servicesManager }) => <AdminServer servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/services',
      children: ({ servicesManager }) => <ServicePanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/modalities',
      children: ({ servicesManager }) => <ModalityPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/equipments',
      children: ({ servicesManager }) => <EquipmentPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/slots',
      children: ({ servicesManager }) => <SlotPanel servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/bot',
      children: ({ servicesManager }) => <BotConnection servicesManager={servicesManager} />,
      roles: ['admin'],
    },
    {
      path: '/configuration/templates',
      children: ({ servicesManager }) => <TemplatesPanel servicesManager={servicesManager} />,
      roles: ['admin', 'medico general', 'radiologo', 'radiologiaInterno'],
    },
  ];
  console.log('Registering worklist route', routerBasename, path);
  const customRoutes: any = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    ...InRoutesFront,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];
  const localStr = localStorage.getItem('usuario');
  const session = localStr ? JSON.parse(localStr) : null;
  const tiposUsuario = session ? session.user.role : ''; // Obtiene el rol del usuario

  // Verificar si el usuario tiene acceso a la ruta basada en el rol
  const checkRoleAccess = (allowedRoles: string[], userRole: string) => {
    return allowedRoles.includes(userRole);
  };

  const AccessDenied = () => (
    <div className="flex h-screen items-center justify-center p-5">
      <div className="bg-primary-main rounded-md border p-6 text-center text-white">
        <h1>Acceso Denegado</h1>
        <p>No tienes permisos para acceder a esta página.</p>
        <a
          href="/"
          className="text-blue-500 hover:underline"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
  const RoleBasedRoute = ({ route, userRole, ...rest }) => {
    // Si el usuario no tiene el rol adecuado, redirige a la página de acceso denegado
    if (!checkRoleAccess(route.roles, userRole)) {
      return <AccessDenied />;
    }

    // Si tiene acceso, renderiza la ruta correspondiente
    return (
      <RouteWithErrorBoundary
        route={route}
        {...rest}
      />
    );
  };
  function RouteWithErrorBoundary({ route, ...rest }) {
    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary context={`Route ${route.path}`}>
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        const isRoleProtected = route.roles && route.roles.length > 0;

        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                {isRoleProtected ? (
                  <RoleBasedRoute
                    route={route}
                    userRole={tiposUsuario}
                  />
                ) : (
                  <RouteWithErrorBoundary route={route} />
                )}
              </PrivateRoute>
            }
          />
        ) : (
          <Route
            key={i}
            path={route.path}
            element={
              isRoleProtected ? (
                <RoleBasedRoute
                  route={route}
                  userRole={tiposUsuario}
                />
              ) : (
                <RouteWithErrorBoundary route={route} />
              )
            }
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
